import { zodResolver } from "@hookform/resolvers/zod";
import { createFeedbackBodyZod } from "@nlc/shared-types";
import { Paperclip, X } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";

import type { CreateFeedbackFormData } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue.ts";
import { FileUploaded } from "@/features/request/feedback-tool/feedbackTool/components/FileUploaded.tsx";
import { Loading } from "@/features/request/feedback-tool/feedbackTool/components/Loading.tsx";
import { useCreateFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useUploadAssets } from "@/features/request/feedback-tool/feedbackTool/hooks/useUploadAssets.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useUser } from "@/hooks/useUser.tsx";

import { TextareaEditor } from "../../components/TextareaComment";
import { useLoadingMutation } from "../../hooks/useLoadingMutation";

function clampDelta(start: number, end: number, min: number, max: number) {
  // ponytail: oversized balloon pins to the start edge; clamping both edges
  // would alternate signs and never settle.
  if (end - start > max - min) return min - start;
  if (end > max) return max - end;
  if (start < min) return min - start;

  return 0;
}

export function Form() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const balloonRef = useRef<HTMLDivElement | null>(null);
  const { data } = useUser();
  const [formPosition, setFormPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const {
    divImgRef,
    formFeedBack,
    img,
    isTablet,
    pdfContainerRef,
    pdfScrollerRef,
    setFormFeedBack: setFormFeedback,
    zoom,
  } = useFeedbackTool();
  const [createError, setCreateError] = useState("");
  const [renderAssets, setRenderAssets] = useState<
    Array<{ attachmentId: number; name: string }>
  >([]);
  const { data: request } = useGetRequest();
  const requestId = request?.id;

  const formMethods = useForm<CreateFeedbackFormData>({
    defaultValues: {
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id ?? 0,
    },
    resolver: zodResolver(createFeedbackBodyZod),
  });

  const assets = formMethods.watch("assets") || [];

  const { handleFileChange, handleFileDelete } = useUploadAssets(
    "assets",
    assets,
    () => {},
    formMethods,
  );

  const isUploading = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "uploading",
  );

  const isError = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "error",
  );

  const { mutate } = useCreateFeedback(img.id, Number(requestId));
  const isLoading = useLoadingMutation("CREATE", img.id);

  const [, forceScrollSync] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    const scroller = pdfScrollerRef.current;

    if (!scroller) return;

    const handlePointerMove = (event: MouseEvent) => {
      if (event.buttons !== 1) return;
      forceScrollSync();
    };

    scroller.addEventListener("mousemove", handlePointerMove);

    return () => scroller.removeEventListener("mousemove", handlePointerMove);
  }, [pdfScrollerRef.current]);

  useEffect(() => {
    const convert = (item: number) => `${item * 100}%`;

    if (formFeedBack) {
      formMethods.setValue(
        "coordinates",
        `${convert(Number(formFeedBack?.y))},${convert(Number(formFeedBack?.x))}`,
      );
    }
  }, [formFeedBack]);

  useEffect(() => {
    if (assets.length > 0) {
      const removeErrors = assets.filter(
        (item) =>
          (item as { status: "error" | "success" | "uploading" }).status !==
          "error",
      );

      const includeAttachments = removeErrors.map((att) => ({
        attachmentId: att.attachmentId ?? 0,
        name: (att as { file: File }).file.name,
      }));

      setRenderAssets(includeAttachments);
      formMethods.setValue("attachments", includeAttachments);

      setFormFeedback({
        attachments: includeAttachments,
        description: formFeedBack?.description,
        format: "IMAGE",
        id: 0,
        imgId: img.id,
        storyId: 0,
        type: "ADD",
        user: {
          id: 0,
          name: "",
        },
        x: formFeedBack?.x ?? 0,
        y: formFeedBack?.y ?? 0,
      });
    }
  }, [assets]);

  useEffect(() => {
    const pdfContainer = pdfContainerRef.current;

    if (!pdfContainer || !formFeedBack) return;

    const right =
      pdfContainerRef.current.clientWidth -
        Number(formFeedBack?.x) * pdfContainerRef?.current?.clientWidth >
      320;
    const bottomFull =
      Number(formFeedBack?.y) * pdfContainerRef?.current?.clientHeight >
      pdfContainerRef?.current?.clientHeight - 220;

    setFormPosition({
      left: right
        ? pdfContainer.offsetLeft +
          Number(formFeedBack?.x) * pdfContainer.clientWidth
        : pdfContainer.offsetLeft +
          Number(formFeedBack?.x) * pdfContainer.clientWidth -
          285,
      top: bottomFull
        ? pdfContainerRef?.current.offsetTop +
          Number(formFeedBack?.y) * pdfContainer.clientHeight -
          150
        : pdfContainerRef?.current.offsetTop +
          Number(formFeedBack?.y) * pdfContainer.clientHeight +
          40,
    });
  }, [formFeedBack, zoom]);

  useLayoutEffect(() => {
    const balloon = balloonRef.current;
    const container = divImgRef.current;

    if (!isTablet || !balloon || !container || !formPosition) return;

    const margin = 8;
    const balloonRect = balloon.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const dx = clampDelta(
      balloonRect.left,
      balloonRect.right,
      containerRect.left + margin,
      containerRect.right - margin,
    );
    const dy = clampDelta(
      balloonRect.top,
      balloonRect.bottom,
      containerRect.top + margin,
      containerRect.bottom - margin,
    );

    if (dx || dy) {
      setFormPosition((prev) =>
        prev ? { left: prev.left + dx, top: prev.top + dy } : prev,
      );
    }
  }, [formPosition, isTablet]);

  useEffect(() => {
    if (isError) {
      toast.add(
        {
          description: "Unsupported file extension.",
          title: "Unsupported Media Type",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  }, [isError]);

  useEffect(() => resetForm(), [img.id]);

  const resetForm = () => {
    formMethods.reset({
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id ?? 0,
    });
  };

  const pdfDotLeft =
    pdfContainerRef.current && formFeedBack?.x
      ? pdfContainerRef.current.offsetLeft +
        Number(formFeedBack.x) * pdfContainerRef.current.offsetWidth -
        (pdfScrollerRef.current?.scrollLeft ?? 0)
      : null;
  const pdfContentLeftBound = pdfContainerRef.current?.offsetLeft ?? 0;
  const pdfContentRightBound =
    pdfContentLeftBound + (pdfScrollerRef.current?.clientWidth ?? 0);
  const isPdfPointVisible =
    pdfDotLeft === null
      ? true
      : pdfDotLeft >= pdfContentLeftBound && pdfDotLeft <= pdfContentRightBound;

  return (
    <>
      {formFeedBack?.format === "PDF" && isPdfPointVisible ? (
        <div
          className="absolute border-2 bg-white border-black border-dashed w-7 h-7 flex justify-center items-center rounded-full z-[2] shadow-xl"
          style={{
            left:
              pdfContainerRef?.current?.clientWidth && formFeedBack?.x
                ? pdfContainerRef?.current.offsetLeft +
                  Number(formFeedBack?.x) *
                    pdfContainerRef?.current?.offsetWidth -
                  (pdfScrollerRef.current?.scrollLeft ?? 0)
                : 0,
            top:
              pdfContainerRef?.current?.clientHeight && formFeedBack?.y
                ? pdfContainerRef?.current.offsetTop +
                  Number(formFeedBack?.y) *
                    pdfContainerRef?.current?.offsetHeight
                : 0,
          }}
        />
      ) : null}
      {formPosition && formFeedBack?.type === "ADD" && isPdfPointVisible ? (
        <div
          className={`absolute bg-bg-primary w-80 p-4 rounded-md shadow-lg z-[9999] origin-top-left ${isTablet ? "max-w-[calc(100vw-2rem)]" : ""}`}
          data-name="box-image"
          onClick={(event) => {
            event.stopPropagation();
          }}
          ref={balloonRef}
          style={{
            left:
              (formPosition?.left ?? 0) -
              (pdfScrollerRef.current?.scrollLeft ?? 0),
            top: formPosition?.top,
          }}
        >
          <div className="flex items-start">
            <Controller
              control={formMethods.control}
              name="comment"
              render={({ field: { name, onChange, value } }) => (
                <TextareaEditor name={name} onChange={onChange} value={value} />
              )}
            />
            <Button
              aria-label="Scope help"
              className="h-min cursor-default"
              onPress={() => {
                setFormFeedback(null);
                setFormPosition(null);
              }}
              variant="unstyled"
            >
              <X className="text-slate-400 mb-3 cursor-pointer" size={24} />
            </Button>
          </div>
          <span className="block text-sm font-normal text-text-error-primary mt-sm">
            {createError}
          </span>
          <div>
            {renderAssets.length > 0
              ? renderAssets.map((asset) => (
                  <FileUploaded
                    key={asset.attachmentId}
                    name={asset.name}
                    onDelete={() => handleFileDelete(asset?.attachmentId ?? 0)}
                  />
                ))
              : null}
          </div>
          <div className="flex mt-4">
            <Button
              className="bg-base"
              onPress={() => {
                inputRef.current?.click();
              }}
              variant="secondary"
            >
              {isUploading ? (
                <Loading />
              ) : (
                <>
                  <input
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                    ref={inputRef}
                    type="file"
                  />
                  <Paperclip
                    className="text-slate-400 cursor-pointer"
                    size={24}
                  />
                </>
              )}
            </Button>
            <Button
              className="w-full ml-2"
              isDisabled={!!isLoading}
              onPress={() => {
                mutate(formMethods.getValues(), {
                  onError: (err: unknown) => {
                    const comment =
                      "body/comment must NOT have fewer than 1 characters";
                    const message = err instanceof Error ? err.message : "";

                    if (message === comment) {
                      setCreateError("Message is required");

                      return toast.add(
                        {
                          description: "Message is required",
                          title: "Feedback not updated",
                          type: "warning",
                        },
                        {
                          timeout: 5000,
                        },
                      );
                    }
                    toast.add(
                      {
                        description: message,
                        title: "Feedback not updated",
                        type: "error",
                      },
                      {
                        timeout: 5000,
                      },
                    );
                  },
                  onSuccess: () => {
                    setRenderAssets([]);
                    setFormPosition(null);
                    setFormFeedback(null);
                    setCreateError("");
                    resetForm();
                  },
                });
              }}
              type="button"
            >
              {isLoading ? <Loading /> : "Add Feedback"}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
